import { useState } from 'react'

import { StandardError } from '@/modules/errors'
import { importFromCloud } from '@/modules/upload/cloud/importFromCloud'
import {
  PickerPageError,
  type PickerPageFailure,
} from '@/modules/upload/cloud/pickerPage/pickerPageSource'
import type { CloudSource, PickedCloudFile } from '@/modules/upload/cloud/types'
import type { AddFiles } from '@/modules/upload/types'
import { captureException } from '@/services/monitoring/captureException'

type ImportStage = 'idle' | 'choosing' | 'getting-files'

interface FailureNotice {
  id: number
  message: string
}

interface CloudImportSettings {
  source: CloudSource
  addFiles: AddFiles
  onImportChange?: (isImporting: boolean) => void
}

type ShowFailures = (messages: string[]) => void

// The list is replaced whole on every import, so a position is a stable key.
const toNotices = (messages: string[]): FailureNotice[] =>
  messages.map((message, position) => ({ id: position, message }))

const openFailureOf = (error: unknown): PickerPageFailure =>
  error instanceof PickerPageError ? error.failure : 'picker-failed'

const openFailureMessage = (source: CloudSource, failure: PickerPageFailure) =>
  failure === 'popup-blocked'
    ? `Your browser blocked the ${source.label} window. Allow pop-ups for this site and try again.`
    : `Could not open ${source.label}. Try again.`

const reportPickerFailure = (
  source: CloudSource,
  error: unknown,
  failure: PickerPageFailure,
) =>
  captureException(
    new StandardError(`Could not open ${source.label}`, 0, {
      provider: source.label,
      failure,
      errorName: error instanceof Error ? error.name : typeof error,
    }),
  )

// A picker that cannot open picks nothing; the customer sees why and monitoring hears of it.
const pickOrExplain = async (
  source: CloudSource,
  showFailures: ShowFailures,
) => {
  try {
    return await source.pick()
  } catch (error) {
    const failure = openFailureOf(error)
    reportPickerFailure(source, error, failure)
    showFailures([openFailureMessage(source, failure)])
    return []
  }
}

export const useCloudImport = ({
  source,
  addFiles,
  onImportChange,
}: CloudImportSettings) => {
  const [stage, setStage] = useState<ImportStage>('idle')
  const [failureNotices, setFailureNotices] = useState<FailureNotice[]>([])
  const showFailures = (messages: string[]) =>
    setFailureNotices(toNotices(messages))

  const bringIn = async (pickedFiles: PickedCloudFile[]) => {
    showFailures([])
    setStage('getting-files')
    showFailures(await importFromCloud(source, pickedFiles, addFiles))
  }

  // Busy from the click, so a second click cannot open the picker again.
  const pickFiles = async () => {
    setStage('choosing')
    onImportChange?.(true)
    try {
      const pickedFiles = await pickOrExplain(source, showFailures)
      if (pickedFiles.length > 0) await bringIn(pickedFiles)
    } finally {
      source.endImport?.()
      setStage('idle')
      onImportChange?.(false)
    }
  }

  return { stage, failureNotices, pickFiles }
}
